/**
 * AmenitiesStep Component
 *
 * Step 3 of listing wizard: amenities selection.
 */

import React from 'react';
import { ListingFormData } from '../../hooks';
import { useTaxonomies, AmenityItem } from '../../hooks';

interface AmenitiesStepProps {
  data: ListingFormData;
  onToggleAmenity: (amenityId: string) => void;
}

const categoryLabels: Record<string, string> = {
  essentials: 'Essentials',
  features: 'Property Features',
  kitchen: 'Kitchen',
  safety: 'Safety',
  outdoor: 'Outdoor & Views',
};

const categoryOrder = ['essentials', 'features', 'kitchen', 'outdoor', 'safety'];

export const AmenitiesStep: React.FC<AmenitiesStepProps> = ({
  data,
  onToggleAmenity,
}) => {
  const { data: taxonomies, isLoading } = useTaxonomies();

  // Group amenities by category
  const groupedAmenities = React.useMemo(() => {
    if (!taxonomies?.amenities) return {};
    return taxonomies.amenities.reduce((acc, amenity) => {
      if (!acc[amenity.category]) {
        acc[amenity.category] = [];
      }
      acc[amenity.category].push(amenity);
      return acc;
    }, {} as Record<string, AmenityItem[]>);
  }, [taxonomies]);

  // Popular amenities
  const popularAmenities = taxonomies?.amenities.filter((a) => a.isPopular) || [];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-gray-100 rounded animate-pulse" />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {[...Array(9)].map((_, i) => (
            <div key={i} className="h-14 bg-gray-100 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          What amenities do you offer?
        </h3>
        <p className="text-gray-600">
          Select all the amenities available at your property.
        </p>
      </div>

      {/* Popular Amenities */}
      {popularAmenities.length > 0 && (
        <div>
          <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
            <StarIcon className="w-5 h-5 text-yellow-500" />
            Popular amenities
          </h4>
          <div className="flex flex-wrap gap-2">
            {popularAmenities.map((amenity) => (
              <AmenityCheckbox
                key={amenity.id}
                amenity={amenity}
                isSelected={data.amenityIds.includes(amenity.id)}
                onToggle={() => onToggleAmenity(amenity.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* All Amenities by Category */}
      {categoryOrder.map((category) => {
        const amenities = groupedAmenities[category];
        if (!amenities?.length) return null;

        return (
          <div key={category}>
            <h4 className="font-medium text-gray-900 mb-3">
              {categoryLabels[category]}
            </h4>
            <div className="flex flex-wrap gap-2">
              {amenities.map((amenity) => (
                <AmenityCheckbox
                  key={amenity.id}
                  amenity={amenity}
                  isSelected={data.amenityIds.includes(amenity.id)}
                  onToggle={() => onToggleAmenity(amenity.id)}
                />
              ))}
            </div>
          </div>
        );
      })}

      {/* Selection Summary */}
      <div className="bg-gray-50 rounded-xl p-4">
        <p className="text-sm text-gray-600">
          <strong>{data.amenityIds.length}</strong> amenities selected
        </p>
        {data.amenityIds.length === 0 && (
          <p className="text-sm text-yellow-600 mt-1">
            Adding amenities helps guests find your listing
          </p>
        )}
      </div>
    </div>
  );
};

// Amenity Checkbox Component
interface AmenityCheckboxProps {
  amenity: AmenityItem;
  isSelected: boolean;
  onToggle: () => void;
}

const AmenityCheckbox: React.FC<AmenityCheckboxProps> = ({
  amenity,
  isSelected,
  onToggle,
}) => (
  <button
    type="button"
    onClick={onToggle}
    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
      isSelected
        ? 'border-primary-500 bg-primary-50 text-primary-700'
        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
    }`}
  >
    {isSelected && <CheckIcon className="w-3.5 h-3.5 text-primary-500" />}
    {amenity.name}
  </button>
);

// Icons
const StarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
  </svg>
);

export default AmenitiesStep;
